import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import PillTitle from "../../../components/pillTitle/pillTitle";
import UserAvatar from "../../../components/userAvatar/userAvatar";
import type { ProfilePanelModel } from "../../../type/user";
import { fetchWithAuth } from "../../../auth/auth";
import { useToast } from "../../../components/toast/toast";
import { getApiUrl } from "../../../utils/getApiUrl";
import DeleteAccountModal from "../../../modal/deleteAccountModal/deleteAccountModal";
import FormActionButton from "../../../components/formActionButton/formActionButton";
import { useI18n } from "../../../utils/translate/i18n/useI18n";
import "../userSettings.scss";
import "./profilePanel.scss";


export default function ProfilePanel({
  user,
  username,
  avatar,
  onUsernameChange,
  onAvatarChange,
}: ProfilePanelModel) {
  const { t } = useI18n();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const usernameEditorRef = useRef<HTMLDivElement>(null);
  const { showToast } = useToast();
  const navigate = useNavigate();

  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [pwSaving, setPwSaving] = useState(false);
  const [resetSending, setResetSending] = useState(false);
  const [isEditingUsername, setIsEditingUsername] = useState(false);
  const [draftUsername, setDraftUsername] = useState(username ?? "");
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  useEffect(() => {
    if (!isEditingUsername) {
      setDraftUsername(username ?? "");
    }
  }, [isEditingUsername, username]);

  const commitUsername = useCallback(() => {
    const normalizedUsername = draftUsername.trim();

    setIsEditingUsername(false);
    if (!normalizedUsername || normalizedUsername === username) {
      setDraftUsername(username ?? "");
      return;
    }

    onUsernameChange(normalizedUsername);
  }, [draftUsername, onUsernameChange, username]);

  useEffect(() => {
    if (!isEditingUsername) {
      return;
    }

    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) {
        return;
      }

      if (!usernameEditorRef.current?.contains(target)) {
        commitUsername();
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
    };
  }, [commitUsername, isEditingUsername]);

  const handleAvatarFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      onAvatarChange((ev.target?.result as string) ?? "");
    };
    reader.readAsDataURL(file);
  };

  const handleChangePassword = async () => {
    if (!newPassword) {
      showToast(t("Nowe hasło jest wymagane."), "error");
      return;
    }
    if (newPassword !== confirmPassword) {
      showToast(t("Hasła nie są zgodne."), "error");
      return;
    }

    setPwSaving(true);
    try {
      const res = await fetchWithAuth(getApiUrl("auth/change-password"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ iduser: user.iduser, oldPassword, newPassword }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error((data as { error?: string }).error ?? `HTTP ${res.status}`);
      }
      showToast(t("Hasło zostało zmienione!"), "info");
      setOldPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : t("Zmiana hasła nie powiodła się."), "error");
    } finally {
      setPwSaving(false);
    }
  };

  const handleSendPasswordReset = async () => {
    if (!user.email) {
      showToast(t("Brak adresu e-mail dla tego konta."), "error");
      return;
    }

    setResetSending(true);
    try {
      const res = await fetchWithAuth(getApiUrl("auth/request-password-reset"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: user.email }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error((data as { error?: string }).error ?? `HTTP ${res.status}`);
      }

      showToast(t("Wysłaliśmy link do zresetowania hasła na Twój adres e-mail."), "info");
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : t("Nie udało się wysłać linku resetującego."), "error");
    } finally {
      setResetSending(false);
    }
  };

return (
  <>
  <div className="app-content__panelleft__container">

    {/* 🔷 HEADER / HERO */}
    <div className="profile-panel__header">
      <div className="profile-panel__header-info">
        <PillTitle variant="blue-brighter" className="profile-panel__header__pill" text={t("Mój profil")} />

        <div className="profile-panel__username-row" ref={usernameEditorRef}>
          {isEditingUsername ? (
            <input
              type="text"
              className="form-control profile-panel__username-input"
              value={draftUsername}
              onChange={(e) => setDraftUsername(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  commitUsername();
                }
                if (e.key === "Escape") {
                  setDraftUsername(username ?? "");
                  setIsEditingUsername(false);
                }
              }}
              autoFocus
            />
          ) : (
            <p className="profile-panel__username">
              {username ?? t("Brak nazwy")}
            </p>
          )}

          <FormActionButton
            type="button"
            aria-label={isEditingUsername ? t("Zapisz nazwę użytkownika") : t("Edytuj nazwę użytkownika")}
            title={isEditingUsername ? t("Zapisz nazwę użytkownika") : t("Edytuj nazwę użytkownika")}
            className="profile-panel__username-edit"
            variant="ghost"
            size="sm"
            iconOnly
            iconClassName={isEditingUsername ? "bi bi-floppy-fill" : "bi bi-pencil-square"}
            onClick={() => {
              if (isEditingUsername) {
                commitUsername();
              } else {
                setDraftUsername(username ?? "");
                setIsEditingUsername(true);
              }
            }}
          />
        </div>

        <p className="profile-panel__email">
          {user.email ?? ""}
        </p>
      </div>

      <div
        className="profile-panel__avatar-wrapper"
        onClick={() => fileInputRef.current?.click()}
        title={t("Zmień avatar")}
      >
        <UserAvatar avatar={avatar} username={username} classname="profile-panel__avatar-img" />
        <div className="profile-panel__avatar-overlay">
          <i className="bi bi-camera-fill"></i>
        </div>
      </div>
    </div>

    <div className="profile-panel__body">
    <input
      ref={fileInputRef}
      type="file"
      accept="image/*"
      className="d-none"
      onChange={handleAvatarFileChange}
    />


    {/* 🔷 PASSWORD SECTION */}
    {user.login_type === 1 && (
      <div className="profile-panel__section profile-panel__section--password">
        <div className="profile-panel__section-title">
          <i className="bi bi-lock"></i>
          {t("Zmiana hasła")}
        </div>

        <div className="profile-panel__field">
          <label>{t("Aktualne hasło")}</label>
          <input
            type="password"
            className="form-control"
            value={oldPassword}
            onChange={(e) => setOldPassword(e.target.value)}
          />
        </div>

        <div className="profile-panel__field">
          <label>{t("Nowe hasło")}</label>
          <input
            type="password"
            className="form-control"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
          />
        </div>

        <div className="profile-panel__field">
          <label>{t("Potwierdź nowe hasło")}</label>
          <input
            type="password"
            className="form-control"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
          />
        </div>

        <FormActionButton
          fullWidth
          iconClassName="bi bi-shield-lock-fill"
          onClick={handleChangePassword}
          disabled={pwSaving}
          loading={pwSaving}
          loadingLabel={t("Zapisywanie…")}
        >
          {t("Zmień hasło")}
        </FormActionButton>

        <div className="profile-panel__password-help mt-3">
          <FormActionButton
            type="button"
            variant="secondary"
            size="sm"
            fullWidth
            iconClassName="bi bi-envelope-paper-fill"
            onClick={handleSendPasswordReset}
            disabled={resetSending}
            loading={resetSending}
            loadingLabel={t("Wysyłanie linku…")}
          >
            {t("Nie pamiętasz hasła? Wyślij link resetujący")}
          </FormActionButton>

          <FormActionButton
            type="button"
            variant="ghost"
            size="sm"
            fullWidth
            iconClassName="bi bi-box-arrow-up-right"
            onClick={() => navigate(`/reset-password?email=${encodeURIComponent(user.email ?? "")}`)}
          >
            {t("Otwórz stronę resetowania hasła")}
          </FormActionButton>
        </div>
      </div>
    )}

    {/* 🔷 DELETE ACCOUNT SECTION */}
    <div className="profile-panel__section profile-panel__section--danger">
      <div className="profile-panel__section-title profile-panel__section-title--danger">
        <i className="bi bi-trash3"></i>
        {t("Strefa niebezpieczna")}
      </div>
      <p className="profile-panel__danger-desc">
        {t("Trwałe usunięcie konta spowoduje utratę wszystkich danych.")}
      </p>
      <FormActionButton
        type="button"
        className="profile-panel__delete-btn"
        variant="danger"
        fullWidth
        iconClassName="bi bi-trash3-fill"
        onClick={() => setIsDeleteModalOpen(true)}
      >
        {t("Usuń konto")}
      </FormActionButton>
    </div>

    </div>
  </div>

  <DeleteAccountModal
    isOpen={isDeleteModalOpen}
    onClose={() => setIsDeleteModalOpen(false)}
    userId={user.iduser ?? 0}
    username={username}
  />
  </>
);
}
